// Renders a retained Klargo document as the paper it is — the Lovable design's
// ContractPreviewDocument, in the exact design of the Klargo-PDF reference:
//
//   - an A4 sheet with print padding and a diagonal KLARGO watermark;
//   - a meta bar: "Klargo • <document>" on the left, reference and date on the right, closed by a
//     full-width rule;
//   - sections: a semibold heading, then label/value rows in a two-column grid with hairline rules,
//     or justified body copy;
//   - a footer: the company line above a top rule, the page counter beside it.
//
// Presentation only — every value comes from a builder (lib/dealReceipt). The .ds-paper-doc scope
// carries the paper's own colour tokens, so it reads the same on screen and in a downloaded PDF.
import React from "react";

/** Label left, value right, hairline rule under the row. */
const RowField = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-[color:var(--doc-section-border)] py-1 text-[13px]">
    <span className="text-[color:var(--doc-muted)]">{label}</span>
    <span className="text-right font-medium text-[color:var(--doc-text)]">{value}</span>
  </div>
);

const Heading = ({ children }) => (
  <h2 className="m-0 mb-3 text-[17px] font-semibold leading-tight text-[color:var(--doc-text)]">{children}</h2>
);

const Paragraph = ({ children }) => (
  <p className="m-0 text-[13px] leading-relaxed text-[color:var(--doc-muted)]">{children}</p>
);

function Block({ block }) {
  switch (block.kind) {
    case "title":
      return (
        <div className="mb-2">
          <div className="mb-5">
            <h1 className="m-0 text-[22px] font-bold leading-tight text-[color:var(--doc-text)]">{block.title}</h1>
            <p className="m-0 mt-1 text-[13px] text-[color:var(--doc-muted)]">{block.subtitle}</p>
          </div>
          <div className="border-b border-[color:var(--doc-border)]" />
        </div>
      );
    case "meta":
      return (
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          {block.fields.map((field) => <RowField key={field.label} {...field} />)}
        </div>
      );
    case "fields":
      return (
        <div>
          {block.heading && <Heading>{block.heading}</Heading>}
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            {block.fields.map((field) => <RowField key={field.label} {...field} />)}
          </div>
        </div>
      );
    case "clause":
      return (
        <div>
          {block.heading && <Heading>{block.heading}</Heading>}
          <div className="grid gap-3">
            {block.paragraphs.map((paragraph) => <Paragraph key={paragraph}>{paragraph}</Paragraph>)}
          </div>
        </div>
      );
    case "note":
      return (
        <div className="grid gap-2">
          {block.paragraphs.map((paragraph) => (
            <p key={paragraph} className="m-0 text-[11.5px] italic leading-relaxed text-[color:var(--doc-muted)]">{paragraph}</p>
          ))}
        </div>
      );
    case "signoff":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <div className="grid gap-3">
            {block.paragraphs.map((paragraph) => <Paragraph key={paragraph}>{paragraph}</Paragraph>)}
          </div>
          <div className="mt-6 border-t border-[color:var(--doc-border)] pt-4">
            <p className="m-0 text-[12px] font-semibold text-[color:var(--doc-text)]">{block.company}</p>
            <p className="m-0 mt-1 text-[11.5px] text-[color:var(--doc-muted)]">{block.footnote}</p>
          </div>
        </div>
      );
    default:
      return null;
  }
}

/** @param {{ document: object, pageOf: string }} props `pageOf` carries {page} and {total}. */
export default function PaperDocumentView({ document: doc, pageOf }) {
  const total = doc.pages.length;
  return (
    <div className="grid gap-4" data-testid="paper-document">
      {doc.pages.map((page, index) => (
        <article key={index}
          className="ds-paper-doc relative flex min-h-[420px] flex-col bg-white px-[clamp(18px,3.6vw,38px)] pb-[26px] pt-[28px] shadow-[0_1px_3px_rgba(10,20,26,.12)]">
          <div className="ds-paper-watermark" aria-hidden="true">
            <div className="ds-paper-watermark-text">KLARGO</div>
          </div>

          <div className="relative flex flex-1 flex-col" style={{ zIndex: 2 }}>
            <div className="mb-6">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[11.5px]">
                {/* brandLine is already "Klargo • <document>". */}
                <span className="font-semibold text-[color:var(--doc-text)]">{doc.brandLine}</span>
                <span className="text-[color:var(--doc-muted)]">{doc.referenceLine}</span>
              </div>
              <div className="mt-2 border-b border-[color:var(--doc-border)]" />
            </div>

            <div className="grid gap-8">
              {page.blocks.map((block, blockIndex) => <Block key={blockIndex} block={block} />)}
            </div>

            <div className="mt-auto flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-[color:var(--doc-border)] pt-3">
              <span className="text-[11px] text-[color:var(--doc-muted)]">{doc.footerLine ?? ""}</span>
              <span className="text-[11px] tabular-nums text-[color:var(--doc-muted)]">
                {pageOf.replace("{page}", String(index + 1)).replace("{total}", String(total))}
              </span>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
